import {AppParams} from "../application/interfaces";
import {Options, ReceiverInterface} from "./receiver";
import {Collection} from "../lib/event-interface";
import {H5LReceiver} from "./h5l-receiver";
import {HLSReceiver} from "./hls-receiver";
import {WebrtcReceiver} from "./webrtc-receiver";
import {VisitXReceiver} from "./visitx-receiver";
import logging from "../utils/log";

export class ReceiverFactory implements ReceiverInterface
{
	private current: ReceiverInterface;
	private lastReceiverType: string;
	private lastPlayData: any = {}; // the unparsed data passed to the last play request - used for fallbacks
	private currentStream: string = undefined;

	private ready : boolean;
	private TAG = "[Sxc.Chat.ReceiverFactory]";
	private initialized: boolean = false;
	private $container: any;

	private h5l: H5LReceiver;
	private hls: HLSReceiver;
	private webrtc: WebrtcReceiver;
	private visitx: VisitXReceiver;

	private $h5l: JQuery;
	private $hls: JQuery;
	private $webrtc: JQuery;
	private $visitx: JQuery;

	public evt : Collection;

	////////////////////////////////////////////////////////////////////////

	constructor (private params : AppParams)
	{
		this.evt = new Collection("receiver-factory");

		this.h5l = new H5LReceiver(this.params.receiver);
		this.hls = new HLSReceiver(this.params.receiver);
		this.webrtc = new WebrtcReceiver(this.params.receiver);
		this.visitx = new VisitXReceiver(this.params.receiver);

		this.h5l.evt.on("*", (event, ...args) =>
		{
			if (event === "ready" && !this.h5l.isReady()) return;

			this.evt.trigger(event, ...args);
		});

		this.hls.evt.on("*", (event, ...args) =>
		{
			if (event === "ready" && !this.hls.isReady()) return;

			this.evt.trigger(event, ...args);
		});

		this.webrtc.evt.on("*", (event, ...args) =>
		{
			if (event === "ready" && !this.webrtc.isReady()) return;

			this.evt.trigger(event, ...args);
		});

		this.visitx.evt.on("*", (event, ...args) =>
		{
			if (event === "ready" && !this.visitx.isReady()) return;

			this.evt.trigger(event, ...args);
		});
	}

	////////////////////////////////////////////////////////////////////////

	initialize($container)
	{
		if (this.initialized) return;

		this.$container = $container;

		this.$hls = $("<div class='hls' />").appendTo($container);
		this.$h5l = $("<div class='h5l' />").appendTo($container);
		this.$webrtc = $("<div class='webrtc' />").appendTo($container);
		this.$visitx = $("<div class='vx' id='vx' />").appendTo($container);

		this.h5l.initialize(this.$h5l, false);
		this.hls.initialize(this.$hls, false);
		this.webrtc.initialize(this.$webrtc, false);
		this.visitx.initialize(this.$visitx, false);

		this.$container.on("mouseenter", (e) => {
			this.evt.trigger("mouseenter");
		});

		this.$container.on("mouseleave", (e) => {
			this.evt.trigger("mouseleave");
		});

		this.initialized = true;
	}

	isReady()
	{
		return this.ready &&
			this.h5l.isReady() &&
			this.hls.isReady() &&
			this.webrtc.isReady() &&
			this.visitx.isReady();
	}

	isInitialized()
	{
		return this.initialized &&
			this.h5l.isInitialized() &&
			this.hls.isInitialized() &&
			this.webrtc.isInitialized() &&
			this.visitx.isInitialized();
	}

	////////////////////////////////////////////////////////////////////////

	public connect()
	{
		// no-op
	}

	disconnect()
	{
		logging.log(this.TAG, "[disconnect]");

		this.h5l.disconnect();
		this.hls.disconnect();
		this.webrtc.disconnect();
		this.visitx.disconnect();

		this.currentStream = undefined;
	}

	destroy()
	{
		logging.log(this.TAG, "destroy");

		this.h5l.destroy();
		this.hls.destroy();
		this.webrtc.destroy();
		this.visitx.destroy();

		this.currentStream = undefined;
	}

	////////////////////////////////////////////////////////////////////////

	/**
	 * Starts playing a stream
	 * @param data the stream data as reported by chat server
	 * @param useReceiver 'auto': choose automatically, 'last': use the last selected,
	 * 	                  'webrtc'|'h5l'|'hls' : force select one, for non-visitx streams
	 */
	play(data : any, useReceiver : string = 'auto') : void
	{
		if((typeof data.modeSpecific == 'undefined' || typeof data.modeSpecific.main == 'undefined') && typeof data != 'string') { return; }

		if (this.current) { this.current.stop(); }

		// store the last play data (for fallbacks)
		this.lastPlayData = data;

		this.setCurrentFromData(data, useReceiver);  // Kiválasztja a megfelelő sugárzót

		this.clear();  // Alap állapotba helyez a UI-t

		if(this.current == this.h5l) {
			this.$h5l.addClass("sxc-visible");
			this.current.play(data.modeSpecific.main.rtmp.address, data.modeSpecific.main.rtmp.serverID, data.modeSpecific.main.rtmp.streamName);
			this.currentStream = data.modeSpecific.main.rtmp.address+'|'+data.modeSpecific.main.rtmp.serverID+'|'+data.modeSpecific.main.rtmp.streamName;
		} else if(this.current == this.hls) {
			this.$hls.addClass("sxc-visible");
			this.current.play(data.modeSpecific.main.hls.address);
			this.currentStream = data.modeSpecific.main.hls.address;
		} else if(this.current == this.webrtc) {
			this.$webrtc.addClass("sxc-visible");
			this.current.play(data.modeSpecific.main.webrtc.sdpURL, data.modeSpecific.main.webrtc.streamInfo.applicationName, data.modeSpecific.main.webrtc.streamInfo.streamName);
			this.currentStream = data.modeSpecific.main.webrtc.sdpURL+'|'+data.modeSpecific.main.webrtc.streamInfo.applicationName+'|'+data.modeSpecific.main.webrtc.streamInfo.streamName;
		} else if(this.current == this.visitx) {
			this.$visitx.addClass("sxc-visible");
			this.current.play(data);
		}
	}

	/**
	 * Select receiver to use
	 * @param useReceiver 'auto': choose automatically, 'last': use the last selected,
	 * 	                  'webrtc'|'h5l'|'hls' : force select one, for non-visitx streams
	 */
	private setCurrentFromData(data : any, useReceiver : string) : void
	{
		if(typeof data == 'string') { this.current = this.visitx; return; }

		if (useReceiver == 'last') return;

		if(this.params.auth.tokenID == "guest") {
			useReceiver = 'hls';
		}

		let hasRTMP   = typeof data.modeSpecific.main.rtmp != 'undefined';
		let hasH5L    = typeof data.modeSpecific.main.h5l != 'undefined' || typeof data.modeSpecific.main.h5live != 'undefined';
		let hasHLS    = typeof data.modeSpecific.main.hls != 'undefined';
		let hasWebRTC = typeof data.modeSpecific.main.webrtc != 'undefined';

		if ((hasWebRTC === true && useReceiver == 'auto') || useReceiver == 'webrtc') {
			this.lastReceiverType = 'webrtc';
			this.current = this.webrtc;
			return;
		} else if (((hasRTMP === true || hasH5L === true) && useReceiver == 'auto') || useReceiver == 'h5l') {
			this.lastReceiverType = 'h5l';
			this.current = this.h5l;

			let chromeUserAgent = navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
			let chromeVersion   = chromeUserAgent ? parseInt(chromeUserAgent[2], 10) : false;
			if(chromeVersion !== false && chromeVersion == 87) {
				console.log('chrome: v87 - force fallback: hls');

				this.lastReceiverType = 'hls';
				this.current = this.hls;
			}

			return;
		} else if ((hasHLS === true && useReceiver == 'auto') || useReceiver == 'hls') {
			this.lastReceiverType = 'hls';
			this.current = this.hls;
			return;
		}

		// now this is nasty - we didn't find anything suitable - what to do, what to do?
		// report error, that's the way to go

		this.evt.trigger("playFailed", {
			reason: "Can't find receiver to play back stream",
			duringStartup: true,
			stream: 'unknown',
			data: {
				receiver: 'unknown'
			}
		});

	}

	stop():void
	{
		logging.log(this.TAG, "Stop", this.isReady());

		if (this.current) this.current.stop();

		this.currentStream = undefined;
	}

	////////////////////////////////////////////////////////////////////////

	setVolume(volume : number):void
	{
		logging.log(this.TAG, "setVolume", volume);

		this.h5l.setVolume(volume);
		this.hls.setVolume(volume);
		this.webrtc.setVolume(volume);
		this.visitx.setVolume(volume);
	}

	forceMute(forced: boolean): void
	{
		logging.log(this.TAG, "forceMute", forced);

		this.h5l.forceMute(forced);
		this.hls.forceMute(forced);
		this.webrtc.forceMute(forced);
		this.visitx.forceMute(forced);
	}

	////////////////////////////////////////////////////////////////////////

	clear() : void
	{
		this.$h5l.removeClass("sxc-visible");
		this.$hls.removeClass("sxc-visible");
		this.$webrtc.removeClass("sxc-visible");
		this.$visitx.removeClass("sxc-visible");

		this.h5l.clear();
		this.hls.clear();
		this.webrtc.clear();
		this.visitx.clear();
	}

	restoreLastStream() : void
	{
		logging.log(this.TAG, "RestoreLastStream");

		this.current.restoreLastStream();
	}

	/**
	 * @param data
	 * @param triggerPlayingEvent if true, when not changed and playing already, we fire a playing event
	 */
	reStreamIfChanged(data : any, triggerPlayingEvent : boolean = false) : void
	{
		logging.log(this.TAG, "reStreamIfChanged", this.currentStream, data);

		if (!data.modeSpecific || !data.modeSpecific.main) {
			// not connected, no stream data (eg. went offline)
			return;
		}

		if (!this.currentStream) {
			this.play(data);
			return;
		}

		const streamData = data.modeSpecific.main;

		// check if not changed
		if (
				(this.current == this.h5l &&
				streamData.rtmp &&
				this.currentStream == streamData.rtmp.address+'|'+streamData.rtmp.serverID+'|'+streamData.rtmp.streamName)
			||
				(this.current == this.hls &&
				streamData.hls &&
				this.currentStream == streamData.hls.address)
			||
				(this.current == this.webrtc &&
				streamData.webrtc &&
				this.currentStream == streamData.webrtc.sdpURL+'|'+streamData.webrtc.streamInfo.applicationName+'|'+streamData.webrtc.streamInfo.streamName)
		)
		{
			if (triggerPlayingEvent) {
				this.evt.trigger('playing');
			}
			return;
		}

		this.play(data, 'last');
	}

	/**
	 * Checks the data from play failure and initiates a protocol fallback, if possible
	 * This is called from playFailed event handler, when the event was thrown during startup
	 *
	 * @param playFailedData data as sent in playFailed event
	 * @return true, if fallback is possible and is underway (no further processing of the event is necessary)
	 */
	public protocolFallback(playFailedData : any) : boolean
	{
		if (playFailedData && playFailedData.data && playFailedData.data.receiver && playFailedData.data.receiver === 'webrtc') {
			// perform protocol fallback from webrtc only (for now)
			logging.log(this.TAG, 'WebRtc playback failed, activating HLS fallback');
			this.play(this.lastPlayData, 'hls');
			return true;
		}
		return false;
	}

	////////////////////////////////////////////////////////////////////////

	getCurrentName() : string
	{
		return this.current
			? this.current.getCurrentName()
			: "ReceiverFactory";
	}

	public getVideoObject() {
		return this.current.getVideoObject();
	}

	setFlag(s: string, enabled : boolean = false): void
	{
		this.$container[enabled ? "addClass" : "removeClass"](s);
	}
}
